import { AddressResponse, FamilyAccountResponse, MembershipInfoResponse, MonitoredFamilyResponse, PaymentMethodResponse, SchoolIntegrationResponse, SchoolResponse, SchoolUserInfoResponse, UserInfoResponse } from "@core/models/response/account.response"
import { CarerInfo } from "@core/models/response/core"
import { buildDeviceFromResponse } from "./device.converter"
import { buildSchoolGroupFromResponse } from "./group.converter"
import { AccountStatus, AccountType, Address, FamilyAccount, FamilyStatus, GenderType, Membership, MembershipType, MonitoredFamily, PaymentMethod, PaymentMethodType, School, SchoolUser, SchoolUserExemptionReason,
    SchoolUserStatus, SchoolUserType, UserInfo, UserProfile, SchoolIntegrationStatus, SyncMode } from "@core/models/domain/account.domain"
import { ModifyUserProfileRequest } from '@core/models/request/account.request';

function mapSchoolIntegrationStatus(status?: string): SchoolIntegrationStatus | undefined {
    switch (status) {
        case 'NOT_REQUESTED':
            return SchoolIntegrationStatus.NOT_REQUESTED;
        case 'PENDING':
            return SchoolIntegrationStatus.WAITING_FOR_APPROVAL;
        case 'READY':
            return SchoolIntegrationStatus.READY_TO_SYNCHRONIZE;
        case 'SYNCED':
            return SchoolIntegrationStatus.SYNCHRONIZED;
        case 'PARTIALLY_SYNCED':
            return SchoolIntegrationStatus.PARTIALLY_SYNCHRONIZED;
        case 'ERROR':
            return SchoolIntegrationStatus.ERROR;
        default:
            return status as SchoolIntegrationStatus | undefined;
    }
}

export function buildSchoolUserFromResponse(response: SchoolUserInfoResponse): SchoolUser {
    // Convert carerInfo to UserInfo and add to familyMembers array
    const familyMembers: UserInfo[] = [];
    if (response.carerInfo) {
        familyMembers.push(buildUserInfoFromCarerInfo(response.carerInfo));
    }

    return {
        schoolAccountId: response.schoolAccountId,
        uniqueIdentifierNumber: response.uniqueIdentifierNumber,
        upi: response.upi,
        misId: response.misId,
        internalIdentifierCode: response.internalIdentifierCode,
        tutorGroup: response.tutorGroup,
        yearGroup: response.yearGroup,
        userInfo: buildUserInfoFromResponse(response.userInfo),
        familyMembers: familyMembers,
        devices: response.devices?.map(deviceResponse => buildDeviceFromResponse(deviceResponse)) || [],
        schoolGroups: response.schoolGroups?.map(groupResponse => buildSchoolGroupFromResponse(groupResponse)) || [],
        schoolUserType: response.type as SchoolUserType,
        schoolUserStatus: response.status as SchoolUserStatus,
        exemptionReason: response.exemptionReason as SchoolUserExemptionReason,
        exemptionNote: response.exemptionNote
    }
}

export function buildUserInfoFromResponse(response: UserInfoResponse): UserInfo {
    return {
        accountId: response.accountId,
        profileId: response.profileId,
        accountType: response.accountType as AccountType,
        status: response.status as AccountStatus, 
        userAuthenticationId: response.userAuthenticationId,
        family: response.family ? buildFamilyAccountFromResponse(response.family) : undefined,
        carerFamilies: response.carerFamilies?.map(familyResponse => buildMonitoredFamilyFromResponse(familyResponse)) || [],
        firstname: response.firstName,
        lastname: response.lastName,
        gender: response.gender ? response.gender as GenderType : undefined,
        dateOfBirth: response.dateOfBirth ? new Date(response.dateOfBirth) : undefined,
        emailAddress: response.emailAddress,
        country: response.country,
        phoneNumber: response.phoneNumber,
        timezone: response.timezone,
        theme: undefined,
        avatar: response.avatar,
        creationDatetime: new Date(response.creationDatetime),
        lastUpdateDatetime: new Date(response.lastUpdateDatetime),
        subscribedToMessages: response.subscribedToMessages,
        testAccount: response.testAccount,
    }
}

/**
 * Converts a `CarerInfo` object into a `UserInfo` domain object.
 * 
 * This function creates a minimal UserInfo object from CarerInfo, which contains
 * basic carer information (first name, last name, email, phone number).
 * Fields that are not available in CarerInfo are set to default values.
 *
 * @param {CarerInfo} carerInfo - The carer information from the response.
 * @returns {UserInfo} A domain object representing the carer as a UserInfo.
 */
function buildUserInfoFromCarerInfo(carerInfo: CarerInfo): UserInfo {
    return {
        accountId: '', // Not available in CarerInfo
        profileId: '', // Not available in CarerInfo
        accountType: AccountType.CARER,
        status: AccountStatus.ACTIVE, // Default status
        firstname: carerInfo.firstName,
        lastname: carerInfo.lastName,
        emailAddress: carerInfo.email,
        phoneNumber: carerInfo.phoneNumber,
        country: undefined,
        timezone: '', // Not available in CarerInfo
    }
}

export function buildUserProfileFromRequest(request: ModifyUserProfileRequest): UserProfile {
    return {
        accountId: request.accountId,
        firstName: request.firstName,
        lastName: request.lastName,
        gender: request.gender ? request.gender as GenderType : undefined,
        dateOfBirth: request.dateOfBirth ? new Date(request.dateOfBirth.toString()) : undefined,
        emailAddress: request.emailAddress,
        e164phoneNumber: request.e164phoneNumber,
        avatar: request.avatar,
    }
}

/**
 * Converts a `MonitoredFamilyResponse` object into a `MonitoredFamily` domain object.
 *
 * This function maps the properties of the response object to the corresponding fields in the domain object.
 * It also converts date fields such as `validityStartDate`, `validityEndDate`, and `creationDatetime` into JavaScript `Date` objects.
 *
 * @param {MonitoredFamilyResponse} response - The response object containing monitored family information.
 * @returns {MonitoredFamily} A domain object representing the monitored family.
 */
export function buildMonitoredFamilyFromResponse(response: MonitoredFamilyResponse): MonitoredFamily {
    return {
        id: response.id,
        accountId: response.accountId,
        familyId: response.familyId,
        firstname: response.firstname,
        lastname: response.lastname,
        phoneNumber: response.phoneNumber,
        emailAddress: response.emailAddress,
        createdBy: response.createdBy,
        validityStartDate: response.validityStartDate ? new Date(response.validityStartDate) : undefined,
        validityEndDate: response.validityEndDate ? new Date(response.validityEndDate) : undefined,
        creationDatetime: new Date(response.creationDatetime),
    }
}

/**
 * Converts a `FamilyAccountResponse` object into a `FamilyAccount` domain object.
 *
 * This function maps the properties of the response object to the corresponding fields in the domain object.
 * It also converts date fields such as `creationDatetime` and `lastModifiedDatetime` into JavaScript `Date` objects.
 * Additionally, it processes the `membershipInfo` field using the `buildMembershipFromResponse` function.
 *
 * @param {FamilyAccountResponse} response - The response object containing family account information.
 * @returns {FamilyAccount} A domain object representing the family account.
 */
export function buildFamilyAccountFromResponse(response: FamilyAccountResponse): FamilyAccount {
    return {
        id: response.id,
        status: response.status as FamilyStatus,
        createdBy: response.createdBy,
        membershipInfo: response.membershipInfo ? buildMembershipFromResponse(response.membershipInfo) : undefined,
        creationDatetime: new Date(response.creationDatetime),
        lastModifiedDatetime: new Date(response.lastModifiedDatetime),
    }
}

/**
 * Converts a `MembershipInfoResponse` object into a `Membership` domain object.
 *
 * This function maps the properties of the response object to the corresponding fields in the domain object.
 * It also converts date fields such as `startDate` and `expiryDate` into JavaScript `Date` objects.
 * Additionally, it processes the `paymentMethod` field using the `buildPaymentMethodFromResponse` function.
 *
 * @param {MembershipInfoResponse} response - The response object containing membership information.
 * @returns {Membership} A domain object representing the membership.
 */
export function buildMembershipFromResponse(response: MembershipInfoResponse): Membership {
    return {
        type: response.type as MembershipType,
        paymentMethod: response.paymentMethod ? buildPaymentMethodFromResponse(response.paymentMethod) : undefined,
        startDate: new Date(response.startDate),
        expiryDate: response.expiryDate ? new Date(response.expiryDate) : undefined,
        subscribedBy: response.subscribedBy,
    }
}

export function buildPaymentMethodFromResponse(response: PaymentMethodResponse): PaymentMethod {
    return {
        type: response.type as PaymentMethodType,
    }
}

/**
 * Converts a `SchoolResponse` object into a `School` domain object.
 *
 * This function maps the properties of the response object to the corresponding fields in the domain object.
 * It also converts the `address` field by mapping each address response to an `Address` object using the `buildAddressFromResponse` converter.
 * Additionally, it parses the `creationDatetime` and `lastModifiedDatetime` fields into JavaScript `Date` objects.
 *
 * @param {SchoolResponse} response - The response object containing school information.
 * @returns {School} A domain object representing the school.
 */
export function buildSchoolFromResponse(response: SchoolResponse): School {
    return {
        accountId: response.accountId,
        wondeId: response.wondeId,
        urn: response.urn,
        mis: response.mis,
        profileId: response.profileId,
        identifierCode: response.identifierCode,
        name: response.name,
        phoneNumbers: response.phoneNumbers,
        notificationRecipientIds: response.notificationRecipientIds ?? [],
        adminEmailAddresses: response.adminEmailAddresses ?? [],
        logo: response.logo,
        status: response.status as AccountStatus,
        integrationStatus: mapSchoolIntegrationStatus(response.integrationStatus),
        lastIntegrationStatusUpdatedDatetime: response.lastIntegrationStatusUpdatedDatetime ? new Date(response.lastIntegrationStatusUpdatedDatetime) : undefined,
        lastSyncDatetime: response.lastSyncDatetime ? new Date(response.lastSyncDatetime) : undefined,
        syncMode: toSyncMode(response.syncMode),
        timezone: response.timezone,
        address: response.address ? buildAddressFromResponse(response.address) : undefined,
        createdBy: response.createdBy,
        creationDatetime: new Date(response.creationDatetime),
        lastModifiedDatetime: new Date(response.lastModifiedDatetime),
    }
}

/** The backend's sync mode, or undefined when none is chosen or the value is unknown. */
export function toSyncMode(value: string | null | undefined): SyncMode | undefined {
    return value === 'YEAR_GROUP' || value === 'CLASSES' || value === 'MIXED' ? value : undefined;
}

/**
 * Converts a `SchoolIntegrationResponse` payload into the internal `School` domain model.
 *
 * Integration payloads do not expose all School fields used by the app, so we map what is
 * available and provide safe defaults for required domain attributes.
 */
export function buildSchoolFromIntegrationResponse(response: SchoolIntegrationResponse): School {
    const now = new Date();
    return {
        accountId: response.id,
        profileId: '',
        identifierCode: '',
        urn: String(response.urn),
        wondeId: response.wondeId,
        mis: response.mis,
        name: response.name,
        phoneNumbers: [],
        notificationRecipientIds: [],
        adminEmailAddresses: [],
        status: AccountStatus.ACTIVE,
        integrationStatus: SchoolIntegrationStatus.NOT_REQUESTED,
        lastIntegrationStatusUpdatedDatetime: undefined,
        lastSyncDatetime: undefined,
        timezone: response.timezone,
        address: response.address ? buildAddressFromResponse(response.address) : undefined,
        createdBy: '',
        creationDatetime: undefined,
        lastModifiedDatetime: undefined
    }
}

/**
 * Converts an `AddressResponse` object into an `Address` domain object.
 *
 * This function maps the properties of the response object to the corresponding fields in the domain object.
 * Additionally, it parses the `creationDatetime` and `lastModifiedDatetime` fields into JavaScript `Date` objects.
 *
 * @param {AddressResponse} response - The response object containing address information.
 * @returns {Address} A domain object representing the address.
 */
export function buildAddressFromResponse(response: AddressResponse): Address {
    return {
        line1: response.line1,
        line2: response.line2,
        town: response.town,
        postCode: response.postCode,
        countryCode: response.countryCode,
        countryName: response.countryName,
        countryIso31662: response.countryIso31662,
        creationDatetime: new Date(response.creationDatetime),
        lastModifiedDatetime: new Date(response.lastModifiedDatetime),
    }
}